// ═══════════════════════════════════════════════════════════════
// Cover Layout Constants — Single source of truth
// ═══════════════════════════════════════════════════════════════
//
// COVER-001: All cover previews in BookBuilderPage.js MUST match
// RemnantCoverPDF.js output exactly. Change values HERE, not in
// individual components. See DECISION_REGISTER.md COVER-001.
//
// COVER-002: sheet/spine/panel geometry (bleed, wrap, trim, the spine
// lookup table, panel box) lives in coverGeometry.js, not here. This file
// owns per-design content constants (colors, sizes, layout fractions) and
// the derived values HTML previews need. See docs/COVER_ARCHITECTURE.md.
import { getCoverDimensions, mustardPlumOpeningFraction, mustardPlumTextMaxWidthPt, MUSTARD_PLUM_PHOTO_LANDSCAPE_PT, mustardPlumPhotoFramePortraitPt } from './coverGeometry';

// ── Cover designs available for launch ──
// 'mustardplum' is the hand-illustrated wreath cover (Eliane) — a SEPARATE
// design from the older 'floralwrap' (mothersday) full wrap, not a replacement.
// 'slatechevron' (F-308) is a SEAMLESS ALL-OVER GEOMETRIC repeat, so unlike 'mustardplum' it needs
// no wreath-axis or spine-aware placement — it reuses the botanical full-wrap render path, the same
// one 'burgwrap' uses. Art is 6650x4463, identical to floral_final_fullwrap.jpg, which is 350 DPI
// on the 19.00 x 12.75in casewrap sheet.
export const LAUNCH_COVER_DESIGNS = ['charcoal', 'floralwrap', 'burgwrap', 'mustardplum', 'slatechevron'];
export const FULLWRAP_DESIGNS = ['floralwrap', 'burgwrap', 'mustardplum', 'slatechevron'];

// ── Botanical Framed cover (frosted panel + photo) ──
export const BOTANICAL_FRAMED = {
  panelWidthPt: 420,
  panelPadding: '16pt 16pt 10pt 16pt',
  panelBg: '#fdf6ee',
  panelOpacity: 0.88,
  panelBorderRadius: 10,
  panelBorderColor: 'rgba(190,155,120,0.38)',
  titleFontPt: 22,
  titleColor: '#1e0e06',
  photoWidthPt: 380,
  photoHeightPt: 285,
  photoBorderColor: '#7c2d12',
  photoPadding: 3,
  subtitleFontPt: 12,
  subtitleColor: '#5a3820',
  authorFontPt: 11,
  authorColor: '#6a4028',
  yearFontPt: 8,
  yearColor: '#6a4028',
  dividerColor: '#7c2d12',
  dividerWidthPct: '55%',
};

// Derived values for HTML previews (percentage/rem-based)
export const BOTANICAL_FRAMED_PREVIEW = {
  panelWidthPct: '58%',
  panelPadding: '10px 14px 8px',
  photoWidthPct: '85%',
  photoAspect: `${BOTANICAL_FRAMED.photoWidthPt}/${BOTANICAL_FRAMED.photoHeightPt}`,
  titleFontRem: '0.95rem',
  subtitleFontRem: '0.7rem',
  authorFontRem: '0.55rem',
  yearFontRem: '0.45rem',
};

// ── Botanical Classic cover (top-left text panel, no photo) ──
export const BOTANICAL_CLASSIC = {
  panelWidthPt: 155,
  panelPadding: 14,
  panelBg: 'rgba(250,245,237,0.92)',
  panelBorderColor: 'rgba(190,155,120,0.38)',
  titleFontPt: 22,
  titleColor: '#1e0e06',
  subtitleFontPt: 10,
  subtitleColor: '#5a3820',
  authorFontPt: 9.5,
  authorColor: '#6a4028',
  outlineBorderColor: 'rgba(240,230,215,0.6)',
  outlineOffset: 4,
};

// ── Charcoal Framed cover (dark bg + photo) ──
// NOTE: FrontFramedCharcoal in RemnantCoverPDF.js uses BOTANICAL_FRAMED photo
// dimensions (380×285pt landscape) for its photo slot — the old CHARCOAL_FRAMED
// photoWidthPt/photoHeightPt values (340×260) were dead constants, never wired
// into the PDF. They are replaced below by getFramedPhotoSlot() which both the
// PDF and the HTML previews call. The non-photo style constants remain unchanged.
export const CHARCOAL_FRAMED = {
  photoBorderColor: '#2a2520',
  photoBorderWidth: 2,
  photoPadding: 6,
  titleFontPt: 26,
  titleColor: '#f5ece0',
  subtitleFontPt: 14,
  subtitleColor: '#f0e6d4',
  authorFontPt: 13,
  authorColor: '#e0d4be',
  brandFontPt: 13,
  brandColor: '#7c2d12',
};

// ── Charcoal Classic cover (text only, no photo) ──
export const CHARCOAL_CLASSIC = {
  titleFontPt: 30,
  titleColor: '#f5ece0',
  subtitleFontPt: 14,
  subtitleColor: '#f0e6d4',
  authorFontPt: 13,
  authorColor: '#e0d4be',
  brandFontPt: 13,
  brandColor: '#7c2d12',
  dividerColor: '#6b5e4e',
};

// ── Floral Wrap cover (hand-illustrated wreath, precise front-panel registration) ──
//
// FLORAL-001: Source art is floral_final_fullwrap.jpg (6650x4463px), a full
// wrap [back|spine|front] with a floral wreath on the front panel and a
// clear golden "opening" for title/photo. The wreath's opening is NOT at
// the geometric front-panel center.
//
// COVER-002 (see docs/COVER_ARCHITECTURE.md and coverGeometry.js): the
// opening's true center is now SCRIPT-DERIVED (scripts/deriveCoverCenters.mjs
// -> scripts/derive_cover_centers.py, enclosed-hole detection on the actual
// art) instead of hand-measured, and lives in coverGeometry.js
// (MUSTARD_PLUM_SOURCE, mustardPlumOpeningFraction) rather than here as a
// fixed fraction. There is no separate pre-cropped front-panel image
// anymore — RemnantCoverPDF renders ONE page-level stretch of
// `fullWrapImageUrl` (seamless by construction), and content is positioned
// using `mustardPlumOpeningFraction(dim)`, which projects the art's
// measured opening centroid through the same `objectFit: cover` transform
// the art itself goes through, recomputed per page count so registration
// holds at every spine width (the old pre-cropped-image approach was
// spine-independent by construction but produced a visible seam at the
// trim edge where its scale met the page-level stretch's scale).
// Mat thickness (photo edge to outer mat edge) — a STYLE choice (border
// weight), not something the opening's size dictates, so it stays a fixed
// constant across art revisions: 24px at the art's native ppi (350.0392...),
// converted to points: (24 / 350.0392...) * 72. `matTotalPt` (both edges) is
// what mustardPlumPhotoFramePortraitPt() reserves outside the portrait
// photo's own size.
const MUSTARD_MAT_PT = 4.936589737844499;
const MUSTARD_MAT_KEYLINE_PT = 1;
const MUSTARD_MAT_TOTAL_PT = 2 * MUSTARD_MAT_PT;

export const FLORAL_WRAP = {
  // Full-res background for the page-level back+spine+front stretch. This
  // is now the ONLY image drawn for the front panel too (see COVER-002
  // above) — RemnantCoverPDF no longer layers a second, separately-scaled
  // front-panel crop on top of it.
  fullWrapImageUrl: '/covers/floral_final_fullwrap.jpg',
  matPt: MUSTARD_MAT_PT,
  // FLORAL-002 (design/persona panel, validated): mat fill is warm cream,
  // not white, with a thin teal keyline hugging the photo. The keyline is
  // drawn INSIDE the existing matPt footprint (photo -> matKeylinePt of teal
  // -> (matPt - matKeylinePt) of cream -> outer edge), so the total outer
  // mat footprint is IDENTICAL to before — only the fill treatment changed.
  matCreamColor: 'rgb(245,238,222)',
  matKeylinePt: MUSTARD_MAT_KEYLINE_PT,
  // Photo frame sizes (inner, before mat). Landscape is the design/persona
  // loop's approved value (coverGeometry.MUSTARD_PLUM_PHOTO_LANDSCAPE_PT —
  // see that constant's comment for the candidates tested); it fills the
  // opening generously and may touch/overlap the wreath, which is fine for
  // a PHOTO. Portrait (coverGeometry.mustardPlumPhotoFramePortraitPt) is
  // NOT yet loop-approved, and unlike landscape is height-CLAMPED — a tall
  // portrait photo pushes the title above and author/subtitle below toward
  // the wreath, and TEXT (unlike the photo) must not touch the florals
  // (COVER-002c: measured directly by sampling the art under each rendered
  // text run, not assumed).
  photoFrames: {
    landscape: MUSTARD_PLUM_PHOTO_LANDSCAPE_PT,
    portrait: mustardPlumPhotoFramePortraitPt(MUSTARD_MAT_TOTAL_PT),
  },
  titleColor: 'rgb(17,70,75)',
  subtitleColor: 'rgb(95,55,30)',
  // Subtitle/author point sizes — sized to sit proportionately under the big
  // fit-to-width title (the old 11/10pt read tiny next to it). Classic
  // (title-only) runs a touch larger than framed since it has no photo.
  subtitlePt: { framed: 15, classic: 16 },
  authorPt: { framed: 13, classic: 14 },
  // Keeps title/subtitle/author from touching the wreath's branches on
  // either side of the clearing — COMPUTED from the measured thin
  // "text line" safe box (coverGeometry.mustardPlumTextMaxWidthPt), not
  // guessed.
  clearingMaxWidthPt: mustardPlumTextMaxWidthPt(),
};

// Derived values for the HTML 3D preview of the floral wrap (COVER-001).
// All geometry is expressed relative to the front panel (8.5in x 11in =
// 612pt x 792pt) so the preview scales with its rendered pixel size and stays
// pixel-faithful to the PDF regardless of preview dimensions. Padding is given
// as a percentage of panel WIDTH — CSS resolves padding-% against width on all
// four sides, so a single value yields a uniform mat (matches the PDF, whose
// mat is a single scalar `padding`). Font/margin ratios are fractions of the
// panel width; the preview multiplies them by its rendered panel px width.
const _FW_PANEL_W_PT = 8.5 * 72; // 612
export const FLORAL_WRAP_PREVIEW = {
  photoWidthPct: {
    landscape: (FLORAL_WRAP.photoFrames.landscape.widthPt / _FW_PANEL_W_PT) * 100,
    portrait: (FLORAL_WRAP.photoFrames.portrait.widthPt / _FW_PANEL_W_PT) * 100,
  },
  photoAspect: {
    landscape: `${FLORAL_WRAP.photoFrames.landscape.widthPt}/${FLORAL_WRAP.photoFrames.landscape.heightPt}`,
    portrait: `${FLORAL_WRAP.photoFrames.portrait.widthPt}/${FLORAL_WRAP.photoFrames.portrait.heightPt}`,
  },
  matCreamPadPct: ((FLORAL_WRAP.matPt - FLORAL_WRAP.matKeylinePt) / _FW_PANEL_W_PT) * 100,
  keylinePadPct: (FLORAL_WRAP.matKeylinePt / _FW_PANEL_W_PT) * 100,
  titleFramedRatio: 20 / _FW_PANEL_W_PT,
  titleClassicRatio: 26 / _FW_PANEL_W_PT,
  subtitleRatio: { framed: FLORAL_WRAP.subtitlePt.framed / _FW_PANEL_W_PT, classic: FLORAL_WRAP.subtitlePt.classic / _FW_PANEL_W_PT },
  authorRatio: { framed: FLORAL_WRAP.authorPt.framed / _FW_PANEL_W_PT, classic: FLORAL_WRAP.authorPt.classic / _FW_PANEL_W_PT },
  gapRatio: 10 / _FW_PANEL_W_PT,
  dividerWidthRatio: 44 / _FW_PANEL_W_PT,
  clearingMaxWidthPct: (FLORAL_WRAP.clearingMaxWidthPt / _FW_PANEL_W_PT) * 100,
  matCreamColor: FLORAL_WRAP.matCreamColor,
  titleColor: FLORAL_WRAP.titleColor,
  subtitleColor: FLORAL_WRAP.subtitleColor,
};

// The wreath opening's true center (COVER-002) is spine-aware — it is a
// function of pageCount, not a fixed constant — so it cannot be a static
// field on FLORAL_WRAP_PREVIEW like the rest of that object. Both
// RemnantCoverPDF.js (via coverGeometry.mustardPlumOpeningFraction) and this
// preview helper call the SAME projection, so preview and PDF agree at
// whatever page count the caller passes in (COVER-001). Callers that don't
// have a live page-count estimate handy may pass the reference page count
// (24 — the common 24-84pp bucket) for a close approximation; the print PDF
// itself always uses the real page count and is exact.
export function floralWrapOpeningPercent(pageCount) {
  const dim = getCoverDimensions(pageCount);
  const { xFraction, yFraction } = mustardPlumOpeningFraction(dim);
  return { xPct: xFraction * 100, yPct: yFraction * 100 };
}

// Fit-to-width title sizing for the Mustard Plum cover, mirroring the pilot
// (render_cover.py fit_font): large for short titles, shrinking for long ones.
// Calibrated to Lora Bold (the cover font), whose average glyph advance measures
// ~0.5em, so rendered width is about len * 0.5 * fontSize. We solve for the font
// size that fills a target width (about the wreath clearing), clamped so a
// one-word title is not gigantic and a very long one stays readable. Both the
// PDF and the HTML preview call this so the title size matches (COVER-001).
// Returns points.
export function floralTitleFontPt(title, framed) {
  const t = String(title || 'Our Story').trim();
  const targetWidthPt = framed ? 290 : 320;
  // Title-only (classic) allows a larger cap so a one/two-word title commands
  // the clearing instead of floating small; framed stays moderate so it does
  // not overpower the photo below.
  const maxPt = framed ? 50 : 80;
  const minPt = 16;
  const est = targetWidthPt / (0.5 * Math.max(t.length, 1));
  return Math.max(minPt, Math.min(maxPt, est));
}

// Title-only (classic) distributed layout (design option D): the title, a small
// flourish, and the subtitle/author are spread down the clearing so it fills
// the vertical space instead of clumping on the midline. Values are fractions
// of the front-panel height (top of each element). When there is NO subtitle or
// author (a bare title), the components fall back to a centered flourish-framed
// arrangement so the lower area is not left empty. Shared by the PDF and preview.
export const FLORAL_WRAP_TITLEONLY = {
  // Distributed (has subtitle/author) — spread to fill ~three-quarters of the
  // clearing height: title high, flourish mid, subtitle/author low. Nudged down
  // slightly so the block centers in the clearing (was sitting high-of-center).
  titleTop: 0.27,
  flourishTop: 0.495,
  subtitleTop: 0.62,
  authorTop: 0.705,
  // Bare (no subtitle/author) — flourish high, title centered, flourish low,
  // spread down the clearing (not a compact centered cluster) with slightly
  // larger ornaments so a single word does not float in the oval.
  bareFlourishTop: 0.34,
  bareTitleTop: 0.465,
  bareFlourishBottom: 0.66,
  bareFlourishScale: 1.18,
  flourishWidthPt: 120, // fallback; actual width scales with the title (floralFlourishWidthPt)
};

// Flourish width scales with the title's rendered width so it visually belongs
// to the title at every length (a fixed-width rule looks tiny under a long
// title and chunky under a short one). Clamped to a sensible min/max.
export function floralFlourishWidthPt(title) {
  const t = String(title || 'Our Story').trim();
  const fontPt = floralTitleFontPt(title, false);
  const titleW = Math.min(t.length * 0.5 * fontPt, FLORAL_WRAP.clearingMaxWidthPt);
  return Math.max(80, Math.min(210, 0.6 * titleW));
}

// ── Orientation-aware photo slot helper (COVER-001 single source of truth) ──
//
// All cover-photo preview surfaces and PDF components call this function with
// the current coverPhotoOrientation value. Centralising the conditional here
// means there is exactly one place to audit or change the framed photo geometry.
//
// Returns: { widthPt, heightPt, aspect }
//   widthPt / heightPt  — absolute point dimensions used by the PDF
//   aspect              — CSS aspect-ratio string used by HTML preview containers
//
// Backward-compatible default: 'landscape' preserves the existing 380×285 slot.
//
// Portrait slot (285×380): 3:4 ratio — natural for portrait people-photos and
// harmonises with the book cover's own portrait format.
export const getFramedPhotoSlot = (orientation = 'landscape') => {
  if (orientation === 'portrait') {
    return { widthPt: 285, heightPt: 380, aspect: '285/380' };
  }
  // landscape (default, backward-compatible)
  return {
    widthPt: BOTANICAL_FRAMED.photoWidthPt,
    heightPt: BOTANICAL_FRAMED.photoHeightPt,
    aspect: `${BOTANICAL_FRAMED.photoWidthPt}/${BOTANICAL_FRAMED.photoHeightPt}`,
  };
};

// ── Drag-to-reposition frame ──
// photoAspect is orientation-dependent — preview components call
// getFramedPhotoSlot(orientation).aspect directly. REPOSITION_FRAME.maxWidth
// is retained as a layout constant only.
export const REPOSITION_FRAME = {
  photoAspect: `${BOTANICAL_FRAMED.photoWidthPt}/${BOTANICAL_FRAMED.photoHeightPt}`,
  maxWidth: 320,
};
